import React, {useMemo, useState } from "react";
import { Button, Form, Modal, Row, Spinner, Table, Col } from "react-bootstrap";
import _ from 'lodash';
import { useToasts } from "react-toast-notifications";
import { COLOR_APP, COLOR_APP_CANCEL } from "../../../constants";
import { CREATE_ORDERS_FROM_POST_COMMENT_V2 } from "../../../graphql/order/mutation";
import { useMutation } from "@apollo/client";
import { imageRender, thousandSeperate } from "../../../helpers";
import { faTrashCan, faPlusSquare, faMinusSquare, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import AlertModal from "../../alert/AlertModal";

function CreateOrderModalV2({ liveId, showCreateOrderModal, setShowCreateOrderModal, selectedComment, postStocks, setPostStocks, setLiveComments, liveComments, getOrderData, liveSreenType }) {
    const [inputs, setInputs] = useState([{ id: 0, value: '' }]);
    const [dataSet, setDataSet] = useState([]);
    const [showAlert, setShowAlert] = useState(false);
    const [tag, setTag] = useState({ error: false,  message: ''});
    // Compute the timestamp for 7 days ago (non-stateful)
    const sevenDaysAgo = useMemo(() => moment().subtract(7, 'days'), []);
    const now = useMemo(() => moment(), []); // Current timestamp
    // Check if the comment's createdAt is within the last 7 days
    const isCommentTooOld = useMemo(() => {
        if (!selectedComment?.createdAt) return true; // Handle null or undefined createdAt
        return !moment(selectedComment.createdAt).isBetween(sevenDaysAgo, now, null, '[]');
    }, [selectedComment?.createdAt, sevenDaysAgo, now]);

    const { addToast } = useToasts();
    const [createOrders, { loading: createLoading }] = useMutation(CREATE_ORDERS_FROM_POST_COMMENT_V2);

    const addInput = () => {
        const newId = inputs.length > 0 ? Math.max(...inputs.map(i => i.id)) + 1 : 0;
        setInputs([...inputs, { id: newId, value: '' }]);
    };

    const removeInput = (id) => {
        setInputs(inputs.filter(input => input.id !== id));
    };

    const handleInputChange = (id, value) => {
        setInputs(inputs.map(input => 
            input.id === id ? { ...input, value } : input
        ));
    };

    const onSelectStock = async (inputId) => {
        const input = inputs.find(i => i.id === inputId);
        if (!input || !input.value) return;

        const selectedValue = input.value.trim().split("ລະຫັດ: ")[1];
        const checkStock = postStocks.find(stock => stock.cfMessage === selectedValue);
        
        if (checkStock) {
            // Check if item already exists in dataSet
            const existingItem = dataSet.find(item => item.cfMessage === checkStock.cfMessage);
            if (existingItem) {
                setShowAlert(true);
                setTag({error: true, message: "ສິນຄ້ານີ້ຖືກເລືອກແລ້ວ!"});
                return;
            }

            // Add new item with initial amount of 1
            const newItem = {
                ...checkStock,
                amount: 1,
                totalPrice: checkStock.stock.price
            };
            setDataSet(prev => [...prev, newItem]);

            // Clear the input
            handleInputChange(inputId, '');
        }
    };

    const resetInputs = () => {
        setInputs([{ id: 0, value: '' }]);
        setDataSet([]);
    };

    const updateItemAmount = (cfMessage, change) => {
        setDataSet(prev => prev.map(item => {
            if (item.cfMessage === cfMessage) {
                const newAmount = Math.max(1, Math.min(item.amount + change, item.stock.amount));
                return {
                    ...item,
                    amount: newAmount,
                    totalPrice: newAmount * item.stock.price
                };
            }
            return item;
        }));
    };

    const removeItem = (cfMessage) => {
        setDataSet(prev => prev.filter(item => item.cfMessage !== cfMessage));
    };

    const calculateTotal = () => {
        return dataSet.reduce((sum, item) => sum + item.totalPrice, 0);
    };

    const _createOrders = async () => {
        try {
            if (createLoading) return;
            if (dataSet.length === 0) {
                setShowAlert(true);
                setTag({ error: true, message: 'ກະລຸນາເລືອກສິນຄ້າຢ່າງໜ້ອຍ 1 ລາຍການ' });
                return;
            }

            const SHOP_ID = await localStorage.getItem("SHOP");

            // Create array of orders
            const orders = dataSet.map(item => ({
                    type: liveSreenType || "POST",
                    post: liveId,
                    live: liveId,
                    postStock: item.id,
                    liveStock: item.id,
                    stock: item.stock.id,
                    amount: item.amount,
                    productName: item.stock.name,
                    currency: item.stock.currency || "ກີບ",
                    cfMessage: item.cfMessage,
                    fbMessage: selectedComment?.message,
                    commentId: selectedComment?.commentId,
                    price: item.stock.price,
                    originPrice: item.stock.originPrice,
                    totalPrice: item.totalPrice,
                    cfName: selectedComment?.name,
                    shop: SHOP_ID,
                    userFbId: selectedComment?.psid
                }
            ));

            // Implement your order creation logic here
            let _res = await createOrders({
                variables: {
                    where: JSON.stringify({
                        userFbId: selectedComment?.psid, 
                        cfName: selectedComment?.name, 
                        type: liveSreenType || "POST", 
                        page: selectedComment?.pageId, postId: liveId, 
                        liveId: liveId, commentId: selectedComment?.commentId,
                        comment: selectedComment?.message
                    }),
                    data: JSON.stringify(orders)
                },
            });
            const response = JSON.parse(_res?.data?.createOrdersFromCommentV2);
            if (response?.msg === "error") {
                setShowAlert(true);
                setTag({error: true, message: response?.error ?? "ສ້າງອໍເດີ້ບໍ່ສໍາເລັດ! ກະລຸນະລອງໃຫມ່ພາຍຫລັງ"});
            } else {
                resetInputs();
                setShowCreateOrderModal(false);

                // get order data
                getOrderData({
                    variables: {
                        where: {
                            post: liveId,
                            isDeleted: false
                        },
                        skip: 0,
                        limit: 1000
                    }
                });

                // Update post stocks
                const updatePostStocks = postStocks.map((postStock) => {
                    const item = dataSet.find((data) => data.id === postStock.id);
                    
                    if (!item) return postStock; // If no matching item, return original postStock
                    
                    const updatedAmount = parseInt(postStock.amount) - parseInt(item.amount);
                    return { ...postStock, amount: updatedAmount };
                });
                setPostStocks(updatePostStocks);

                // update post comments
                const _updatePostComments = liveComments?.map((_comment) => {
                    if (_comment?.id === selectedComment?.id) return { ..._comment, isCreated: true }

                    return _comment
                })
                setLiveComments(_updatePostComments)
                
                // alert success
                addToast("ສ້າງອໍເດີ້ສໍາເລັດ!", {
                    appearance: 'success',
                    autoDismiss: true,
                })
            }
        } catch (error) {
            console.log("_createOrder Error: ", error);
            setShowAlert(true);
            setTag({error: true, message: "ເກີດຂໍ້ຜິດພາດ!"});
        }
    };

    return (
        <>
            {isCommentTooOld ? (
                <Modal 
                    show={showCreateOrderModal} 
                    onHide={() => {
                        if (createLoading) return;
                        setShowCreateOrderModal(false);
                    }}
                    aria-labelledby="contained-modal-title-vcenter"
                >
                    <Modal.Header closeButton 
                        id='contained-modal-title-vcenter'
                        className="bg-danger text-white"
                    >
                            <Modal.Title>ແຈ້ງເຕືອນ</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p style={{ fontWeight: 'bold', alignSelf: 'center' }}>ບໍ່ສາມາດສົ່ງຂໍຄວາມສຳລັບຄອມເມັ້ນ ທີ່ມີອາຍຸຫລາຍກວ່າ 6ວັນ</p>
                    </Modal.Body>
                </Modal>
            ): (
            <>
                <Modal 
                    show={showCreateOrderModal} 
                    fullscreen={true}
                    size="lg"
                    onHide={() => {
                        if (createLoading) return;
                        setShowCreateOrderModal(false);
                    }}
                >
                    <Modal.Body>
                        <h3 style={{ fontWeight: 'bold', alignSelf: 'center' }}>ສ້າງອໍເດີ້</h3>
                        <Form.Group>
                            <div>ຊື່ລູກຄ້າ</div>
                            <Form.Control value={selectedComment?.name} readOnly />
                        </Form.Group>

                        <div style={{ margin: '20px 0' }}>
                            <Row>
                                <Col md={4}><h5>ເລືອກສິນຄ້າ</h5></Col>
                                <Col md={8}>
                                    <Row>
                                        <Col md={8}>
                                            {inputs.map((input, index) => (
                                                <div key={input.id} className="input-group mb-2">
                                                    <input
                                                        type="text"
                                                        list={`listItem-${input.id}`}
                                                        placeholder="ເລືອກສິນຄ້າ"
                                                        value={input.value}
                                                        onChange={(e) => handleInputChange(input.id, e.target.value)}
                                                        className="form-control form-control-md"
                                                    />
                                                    <datalist id={`listItem-${input.id}`}>
                                                        {postStocks?.map((lStock, idx) => {
                                                            if (lStock?.amount <= 0 || !lStock?.isPublished) return null;
                                                            return (
                                                                <option key={idx} value={"ລະຫັດ: " + lStock?.cfMessage}>
                                                                    {"ສິນຄ້າ: " + lStock?.stock?.name}, {"ຈຳນວນ: " + lStock?.amount}, {"ລາຄາ: " + lStock?.stock?.price}
                                                                </option>
                                                            );
                                                        })}
                                                    </datalist>
                                                    <Button 
                                                        onClick={() => onSelectStock(input.id)}
                                                        style={{ background: COLOR_APP, border: 'none' }}
                                                    >
                                                        ເພີ່ມ
                                                    </Button>
                                                    {inputs.length > 1 && (
                                                        <Button 
                                                            onClick={() => removeInput(input.id)}
                                                            variant="danger"
                                                        >
                                                            ລົບ
                                                        </Button>
                                                    )}
                                                </div>
                                            ))}
                                        </Col>
                                        <Col md={2}>
                                            <Button 
                                                onClick={resetInputs}
                                                variant="outline-secondary"
                                            >
                                                ຣີເຊັດ
                                            </Button>
                                        </Col>    
                                    </Row>
                                </Col>
                            </Row>
                        </div>

                        <Table>
                            <thead style={{ background: "#F3F4F6" }}>
                                <tr>
                                    <th style={{ border: "none" }}>#</th>
                                    <th style={{ border: "none" }}>ຮູບສິນຄ້າ</th>
                                    <th style={{ border: "none" }}>ລະຫັດ CF</th>
                                    <th style={{ border: "none" }}>ຊື່ສິນຄ້າ</th>
                                    <th style={{ border: "none" }}>ຈຳນວນ</th>
                                    <th style={{ border: "none" }}>ລາຄາ</th>
                                    <th style={{ border: "none" }}>ທັງໝົດ</th>
                                    <th style={{ border: "none" }}>ຈັດການ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dataSet.map((item, index) => (
                                    <tr key={index}>
                                        <td style={{ borderBottom: "none" }}>{index + 1}</td>
                                        <td style={{ borderBottom: "none" }}>
                                            <img
                                                alt=""
                                                src={imageRender(item?.stock?.image)}
                                                style={{
                                                    margin: 0,
                                                    padding: 0,
                                                    objectFit: "cover",
                                                    maxHeight: "40px",
                                                }}
                                            />
                                        </td>
                                        <td style={{ borderBottom: "none" }}>{item.cfMessage}</td>
                                        <td style={{ borderBottom: "none" }}>{item.stock.name}</td>
                                        <td style={{ borderBottom: "none" }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <FontAwesomeIcon 
                                                    color={COLOR_APP} 
                                                    icon={faMinusSquare} 
                                                    onClick={() => updateItemAmount(item.cfMessage, -1)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                {item.amount}
                                                <FontAwesomeIcon 
                                                    color={COLOR_APP} 
                                                    icon={faPlusSquare} 
                                                    onClick={() => updateItemAmount(item.cfMessage, 1)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </div>
                                        </td>
                                        <td style={{ borderBottom: "none" }}>
                                            {thousandSeperate(item.stock.price)} {item.stock.currency || "ກີບ"}
                                        </td>
                                        <td style={{ borderBottom: "none" }}>
                                            {thousandSeperate(item.totalPrice)} {item.stock.currency || "ກີບ"}
                                        </td>
                                        <td style={{ borderBottom: "none" }}>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "start",
                                                    alignItems: "center",
                                                    padding: 6,
                                                    color: "red",
                                                    cursor: "pointer"
                                                }}
                                                onClick={() => removeItem(item.cfMessage)}
                                            >
                                                <FontAwesomeIcon className="mr-2" icon={faTrashCan} />
                                                ລົບ
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {dataSet.length > 0 && (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'right', fontWeight: 'bold' }}>ລວມທັງໝົດ:</td>
                                        <td colSpan="2" style={{ fontWeight: 'bold' }}>
                                            {thousandSeperate(calculateTotal())} ກີບ
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>

                        <div style={{ display: 'flex', justifyContent: 'end' }}>
                            <Button
                                onClick={() => {
                                    if (createLoading) return;
                                    resetInputs();
                                    setShowCreateOrderModal(false);
                                }}
                                disabled={createLoading}
                                style={{ background: 'transparent', color: COLOR_APP_CANCEL, borderColor: COLOR_APP_CANCEL }}
                            >
                                ຍົກເລີກ
                            </Button>
                            <Button
                                onClick={_createOrders}
                                style={{ background: COLOR_APP, marginLeft: 10, width: 100, border: 'none' }}
                                disabled={createLoading || dataSet.length === 0}
                            >
                                {createLoading ? <Spinner size="sm" animation="border" /> : "ບັນທືກ"}
                            </Button>
                        </div>
                    </Modal.Body>
                </Modal>
                {/* //* Modal notify */}
                <AlertModal showAlert={showAlert} setShowAlert={setShowAlert} error={tag?.error} message={tag?.message} />
            </>
            )}
        </>
    );
}

export default CreateOrderModalV2;